<script setup lang="ts">
// Reusable regulator chooser. Single-select (Select) by default, or multi-select (MultiSelect)
// with `multiple`. The closed control shows compact acronyms (BCB, CVM…) with each regulator's
// icon; the dropdown shows the icon, acronym and full name. Options are supplied by the caller
// (account's regulators, full catalog, …); the value is the regulator slug (or an array of
// slugs when multiple). Used by the account create/edit dialogs (multiple) and the KYT policy
// regulator (single).
import { computed } from 'vue';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import BaseIcon from '@/components/base/BaseIcon.vue';
import RegulatorOption from '@/components/common/RegulatorOption.vue';
import { regulatorIcon } from '@/constants/regulatorIcons';
import type { Regulator } from '@/types/regulators';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    modelValue: string | string[] | null;
    regulators: Regulator[];
    multiple?: boolean;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    showClear?: boolean;
    /** Slugs that appear greyed out (with an "em breve" hint) and can't be selected. */
    disabledSlugs?: string[];
  }>(),
  {
    multiple: false,
    placeholder: 'Selecionar regulador',
    disabled: false,
    loading: false,
    showClear: true,
    disabledSlugs: () => [],
  },
);

const emit = defineEmits<{ (e: 'update:modelValue', value: string | string[] | null): void }>();

// `label` carries both acronym and name so the built-in filter matches either.
const disabledSet = computed(() => new Set(props.disabledSlugs));
const options = computed(() =>
  props.regulators.map((r) => ({
    label: `${r.slug.toUpperCase()} ${r.name}`,
    value: r.slug,
    slug: r.slug,
    name: r.name,
    disabled: disabledSet.value.has(r.slug),
  })),
);
const nameOf = (slug: string) => props.regulators.find((r) => r.slug === slug)?.name ?? slug;
</script>

<template>
  <MultiSelect
    v-if="multiple"
    :modelValue="(modelValue as string[]) ?? []"
    :options="options"
    optionLabel="label"
    optionValue="value"
    optionDisabled="disabled"
    :placeholder="placeholder"
    :disabled="disabled || loading"
    :loading="loading"
    display="chip"
    filter
    class="w-full"
    v-bind="$attrs"
    @update:modelValue="(v) => emit('update:modelValue', v)"
  >
    <template #chip="{ value }">
      <span class="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-700">
        <BaseIcon size="xs" class="text-slate-500">{{ regulatorIcon(value) }}</BaseIcon>
        {{ String(value).toUpperCase() }}
      </span>
    </template>
    <template #option="{ option }">
      <RegulatorOption :option="option" />
    </template>
  </MultiSelect>
  <Select
    v-else
    :modelValue="(modelValue as string | null)"
    :options="options"
    optionLabel="label"
    optionValue="value"
    optionDisabled="disabled"
    :placeholder="placeholder"
    :disabled="disabled || loading"
    :loading="loading"
    :showClear="showClear"
    class="w-full"
    v-bind="$attrs"
    @update:modelValue="(v) => emit('update:modelValue', (v as string) ?? null)"
  >
    <template #value="{ value, placeholder: ph }">
      <span v-if="value" class="inline-flex items-center gap-1.5 font-semibold">
        <BaseIcon size="xs" class="text-slate-500">{{ regulatorIcon(value) }}</BaseIcon>
        {{ String(value).toUpperCase() }}
        <span class="text-xs font-normal text-slate-500">{{ nameOf(value) }}</span>
      </span>
      <span v-else class="text-slate-400">{{ ph }}</span>
    </template>
    <template #option="{ option }">
      <RegulatorOption :option="option" />
    </template>
  </Select>
</template>
